import {
  AssessmentWindowRef,
  CircleProgramme,
  ProgrammeCheckinAnswer,
  ProgrammeOwnCheckin,
  ProgrammeWeek,
} from 'src/api/programmes';
import { pick } from 'src/i18n/contentLanguage';

/* The programme's copy, English first and Norwegian bokmål when the browser
   is Norwegian (no em-dashes), and the small calculations the Program tab
   shares. Product copy spells "Circle" with a capital C. */

export const TAB_LABEL = pick('Programme', 'Program');

export const LOADING_TEXT = pick(
  'Loading the programme ...',
  'Henter programmet ...'
);
export const UNAVAILABLE_TEXT = pick(
  'Could not load the programme right now.',
  'Kunne ikke hente programmet akkurat nå.'
);

/* Before the leader has started */

export const NOT_STARTED_MEMBER_TITLE = pick(
  'The programme has not started yet',
  'Programmet har ikke startet ennå'
);
export const NOT_STARTED_MEMBER_TEXT = pick(
  "The leader of your Circle starts the programme before the first meeting. Then you will find the week's theme and the week's step here.",
  'Lederen av din Circle starter programmet før første møte. Da finner du ukens tema og ukens steg her.'
);

export const NOT_STARTED_LEADER_TITLE = pick(
  'Start from a programme',
  'Start fra program'
);
export const NOT_STARTED_LEADER_TEXT = pick(
  'The programme gives your Circle one theme a week for the meeting and one small step a week for each participant. You move the group one week at a time, and see who did the step.',
  'Programmet gir din Circle ett tema i uken for møtet og ett lite steg i uken for hver deltaker. Du flytter gruppen én uke om gangen, og ser hvem som har gjort steget.'
);
export const START_LABEL = pick('Start the programme', 'Start programmet');
export const STARTING_LABEL = pick('Starting ...', 'Starter ...');
export const STARTED_TEXT = pick(
  'The programme is under way. The group is in week 1.',
  'Programmet er i gang. Gruppen er i uke 1.'
);
export const START_FAILED_TEXT = pick(
  'The programme was not started. Try again.',
  'Programmet ble ikke startet. Prøv igjen.'
);
export const templateSummary = (weeksTotal: number): string =>
  pick(
    `${weeksTotal} weeks, one meeting a week, one step a week.`,
    `${weeksTotal} uker, ett møte i uken, ett steg i uken.`
  );

/* The week */

export const weekLabel = (week: number, total: number): string =>
  pick(`Week ${week} of ${total}`, `Uke ${week} av ${total}`);
export const MEETING_TITLE = pick("The week's theme", 'Ukens tema');
export const STEP_TITLE = pick("The week's step", 'Ukens steg');
export const COMPLETED_TEXT = pick(
  'The programme is over. Thank you for the effort. The group decides for itself whether it carries on.',
  'Programmet er avsluttet. Takk for innsatsen. Gruppen bestemmer selv om den fortsetter.'
);
export const COMPLETED_KICKER = pick('Completed', 'Avsluttet');

export const MEASURE_HINT: Record<AssessmentWindowRef, string> = pick(
  {
    BEFORE:
      'This week everyone answers the three questions about loneliness. You find them under Measure.',
    MID: 'This week you can answer the three questions again (optional). You find them under Measure.',
    AFTER:
      'This week everyone answers the three questions again. You find them under Measure.',
    FOLLOW_UP:
      'This week everyone answers the three questions one last time. You find them under Measure.',
  },
  {
    BEFORE:
      'Denne uken svarer alle på de tre spørsmålene om ensomhet. Du finner dem under Måling.',
    MID: 'Denne uken kan du svare på de tre spørsmålene igjen (frivillig). Du finner dem under Måling.',
    AFTER:
      'Denne uken svarer alle på de tre spørsmålene igjen. Du finner dem under Måling.',
    FOLLOW_UP:
      'Denne uken svarer alle på de tre spørsmålene en siste gang. Du finner dem under Måling.',
  }
);
export const LEADER_MEASURE_HINT: Record<AssessmentWindowRef, string> = pick(
  {
    BEFORE:
      'The measure before the programme is open this week. Remind the group of the three questions under Measure.',
    MID: 'The midway measure is open this week (optional for the participants).',
    AFTER:
      'The after measure is open this week. Remind the group of the three questions under Measure.',
    FOLLOW_UP: 'The follow-up measure is open this week.',
  },
  {
    BEFORE:
      'Målingen før programmet er åpen denne uken. Minn gruppen på de tre spørsmålene under Måling.',
    MID: 'Midtveismålingen er åpen denne uken (frivillig for deltakerne).',
    AFTER:
      'Ettermålingen er åpen denne uken. Minn gruppen på de tre spørsmålene under Måling.',
    FOLLOW_UP: 'Oppfølgingsmålingen er åpen denne uken.',
  }
);

/* Check-in (members) */

export const CHECKIN_QUESTION = pick(
  'Did you do the step?',
  'Gjorde du steget?'
);
export const CHECKIN_NOTE_LABEL = pick(
  'Want to say something about it? (optional)',
  'Vil du si noe om det? (valgfritt)'
);
export const CHECKIN_NOTE_PLACEHOLDER = pick('A few words', 'Noen få ord');
export const CHECKIN_NOTE_MAX_LENGTH = 280;
export const CHECKIN_SAVE_LABEL = pick('Save', 'Lagre');
export const CHECKIN_SAVING_LABEL = pick('Saving ...', 'Lagrer ...');
export const CHECKIN_SAVED_TEXT = pick(
  'Thank you, your answer is saved.',
  'Takk, svaret ditt er lagret.'
);
export const CHECKIN_FAILED_TEXT = pick(
  'The answer was not saved. Try again.',
  'Svaret ble ikke lagret. Prøv igjen.'
);
export const CHECKIN_CHANGE_LABEL = pick('Change answer', 'Endre svar');
export const CHECKIN_ONLY_LEADER_SEES = pick(
  'The leader sees your answer, so they know who needs a little nudge. Nobody else sees it.',
  'Lederen ser svaret ditt, så hen vet hvem som trenger en liten dytt. Ingen andre ser det.'
);

export const ANSWER_LABEL: Record<ProgrammeCheckinAnswer, string> = pick(
  { YES: 'Yes', PARTLY: 'Partly', NO: 'No' },
  { YES: 'Ja', PARTLY: 'Delvis', NO: 'Nei' }
);
export const ANSWER_ORDER: readonly ProgrammeCheckinAnswer[] = [
  'YES',
  'PARTLY',
  'NO',
];

export const answeredText = (answer: ProgrammeCheckinAnswer): string =>
  pick(
    `You answered ${ANSWER_LABEL[answer].toLowerCase()}.`,
    `Du svarte ${ANSWER_LABEL[answer].toLowerCase()}.`
  );

/* Earlier weeks (members) */

export const EARLIER_WEEKS_TITLE = pick('The weeks so far', 'Ukene så langt');
export const NOT_ANSWERED_LABEL = pick('Not answered', 'Ikke svart');
export const LATER_WEEKS_TEXT = (remaining: number): string =>
  remaining === 1
    ? pick('One week left after this one.', 'Én uke igjen etter denne.')
    : pick(
        `${remaining} weeks left after this one.`,
        `${remaining} uker igjen etter denne.`
      );

/* Leader */

export const LEADER_WEEK_PREVIOUS_LABEL = pick('Previous week', 'Forrige uke');
export const LEADER_WEEK_NEXT_LABEL = pick('Next week', 'Neste uke');
export const LEADER_MOVE_FAILED_TEXT = pick(
  'The week was not changed. Try again.',
  'Uken ble ikke endret. Prøv igjen.'
);
export const movedText = (week: number): string =>
  pick(`The group is now in week ${week}.`, `Gruppen er nå i uke ${week}.`);
export const LEADER_WEEK_TEXT = pick(
  'Move the group on after each meeting. The participants only see the week the group is in, and can answer the step for that week and earlier ones.',
  'Flytt gruppen videre etter hvert møte. Deltakerne ser bare den uken gruppen er i, og kan svare på steget for den og tidligere uker.'
);

export const CHECKINS_TITLE = pick('Who did the step', 'Hvem gjorde steget');
export const CHECKINS_TEXT = pick(
  "The answers to the week's step, participant by participant. Use it to follow up whoever has gone quiet, not to judge anyone.",
  'Svarene på ukens steg, deltaker for deltaker. Bruk det til å følge opp den som har blitt stille, ikke til å vurdere noen.'
);
export const CHECKINS_EMPTY_TEXT = pick(
  'No participants in your Circle yet.',
  'Ingen deltakere i din Circle ennå.'
);
export const checkinsCountText = (answered: number, members: number): string =>
  pick(
    `${answered} of ${members} have answered`,
    `${answered} av ${members} har svart`
  );
export const COUNTS_TITLE = pick('Week by week', 'Uke for uke');
export const COUNT_LEGEND = pick('Yes / Partly / No', 'Ja / Delvis / Nei');
export const UNKNOWN_NAME = pick('No name', 'Uten navn');

export const GUIDE_TITLE = pick('Leader guide', 'Lederguide');
export const GUIDE_SHOW_LABEL = pick(
  'Show the leader guide',
  'Vis lederguiden'
);
export const GUIDE_HIDE_LABEL = pick(
  'Hide the leader guide',
  'Skjul lederguiden'
);

export const COMPLETE_LABEL = pick('Close the programme', 'Avslutt programmet');
export const COMPLETE_CONFIRM_TEXT = pick(
  'Close the programme? The participants cannot answer steps after this. It cannot be undone here.',
  'Avslutte programmet? Deltakerne kan ikke svare på steg etter dette. Det kan ikke angres her.'
);
export const COMPLETED_TOAST_TEXT = pick(
  'The programme is closed.',
  'Programmet er avsluttet.'
);
export const COMPLETE_FAILED_TEXT = pick(
  'The programme was not closed. Try again.',
  'Programmet ble ikke avsluttet. Prøv igjen.'
);

/* Registration (leaders) */

export const REGISTRATION_TITLE = pick('Sign-up', 'Påmelding');
export const REGISTRATION_TEXT = pick(
  'Share the link or the code with the participants. They create an account with their phone number and are in within a minute.',
  'Del lenken eller koden med deltakerne. De lager konto med telefonnummeret sitt og er med på ett minutt.'
);
export const REGISTRATION_LINK_LABEL = pick('Link', 'Lenke');
export const REGISTRATION_CODE_LABEL = pick('Code', 'Kode');
export const COPY_LINK_LABEL = pick('Copy link', 'Kopier lenke');
export const COPY_CODE_LABEL = pick('Copy code', 'Kopier kode');
export const JOIN_PAGE_HINT = (origin: string): string =>
  pick(
    `The participants enter the code at ${origin}/bli-med.`,
    `Deltakerne skriver koden inn på ${origin}/bli-med.`
  );
export const COPIED_TEXT = pick('Copied.', 'Kopiert.');
export const COPY_FAILED_TEXT = pick(
  'Could not copy. Select the text and copy it yourself.',
  'Kunne ikke kopiere. Merk teksten og kopier den selv.'
);
export const REGISTRATION_NO_CODE_TEXT = pick(
  'The code is made when the Circle gets the programme code. Reload the page if it does not show.',
  'Koden lages når Circlen får programkoden. Last siden på nytt om den ikke vises.'
);
export const registrationLink = (origin: string, circleId: string): string =>
  `${origin}/registrering/${encodeURIComponent(circleId)}`;

/* Helpers */

export const currentWeek = (
  programme: Pick<CircleProgramme, 'weeks' | 'current_week'>
): ProgrammeWeek | undefined =>
  programme.weeks.find((week) => week.number === programme.current_week);

export const checkinFor = (
  checkins: readonly ProgrammeOwnCheckin[],
  week: number
): ProgrammeOwnCheckin | undefined =>
  checkins.find((checkin) => checkin.week === week);

/** The weeks a member can look back on: before the current one. */
export const earlierWeeks = (
  programme: Pick<CircleProgramme, 'weeks' | 'current_week'>
): ProgrammeWeek[] =>
  programme.weeks.filter((week) => week.number < programme.current_week);

/**
 * The leader guide is plain text with lines in capitals as headings. Split
 * into blocks so the screen can set the headings apart.
 */
export type GuideBlock =
  { kind: 'heading'; text: string } | { kind: 'paragraph'; text: string };

export const guideBlocks = (guide: string): GuideBlock[] =>
  guide
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n');
      const first = lines[0];
      const isHeading =
        first === first.toUpperCase() &&
        /[A-ZÆØÅ]/.test(first) &&
        lines.length === 1;
      return isHeading
        ? { kind: 'heading', text: first }
        : { kind: 'paragraph', text: block };
    });
